import React, { useContext } from 'react';
import { isEmpty } from 'lodash';

import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import withResponsive from 'components/Layout/withResponsive';
import { Link, withRouter } from 'react-router-dom';
import Tag from 'components/forms/Tag';
import Checkbox from 'components/forms/Checkbox';
import { patchMessage } from 'redux/actions/messageActions';

import { getMessageFromDisplayName } from 'utils/inbox';
import { formatInboxTimestamp } from 'utils/misc';
import { sequenceTabUrl } from 'constants/routing';
import { Can, AbilityContext } from 'utils/permissions';
import classNames from 'classnames';
import { MIN_DESKTOP_WIDTH, MAX_MEDIUM_SIZE } from 'constants/misc';
import { ReactComponent as AccountIcon } from 'icons/account.svg';
import { ReactComponent as SequenceIcon } from 'icons/sequence.svg';
import { ReactComponent as OpportunityIcon } from 'icons/inboxOpportunity.svg';
import { primary100, secondary900 } from 'themes/colors';
import ReplyStatusBadge from './ReplyStatusBadge';
import MessageActionIcons, {
  ACTION_MARK_AS_READ,
  ACTION_ARCHIVE,
  ACTION_DELETE,
} from './MessageActionIcons';


const useStyles = makeStyles(theme => ({
  customLink: {
    color: secondary900,
    textDecoration: 'none',
    '&:focus, &:hover, &:visited, &:link, &:active': {
      color: secondary900,
      textDecoration: 'none',
    },
  },
  outreachWrapper: {
    position: 'relative',
    backgroundColor: theme.palette.secondary['50'],
    padding: '0.75rem 1rem',
    overflow: 'hidden',
    borderBottom: `1px solid ${theme.palette.secondary['200']}`,
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[0],
      position: 'relative',
      zIndex: '1',
      '& $readDot': {
        display: 'block',
      },
      '& $unselectedCheckbox': {
        display: 'block',
      },
      '& $messageAction': {
        right: 0,
      },
      '& $outreachDate': {
        display: 'none',
      },
    },
    '&$outreachUnread': {
      backgroundColor: theme.palette.white,
      '& $flexContainer': {
        '& $outreachData': {
          '& $messageFrom': {
            fontWeight: '600',
          },
        },
        '& $outreachMessage': {
          '& $messageSubject': {
            fontWeight: '600',
          },
        },
      },
      '& $outreachDate': {
        fontWeight: '600',
        color: theme.palette.secondary.text,
      },
      '& $fromName': {
        fontWeight: 600,
        marginRight: '0.5rem',
        display: 'inline-block',
      },
    },
    '&$outreachSelected': {
      backgroundColor: primary100,
    },
  },
  outreachUnread: {},
  outreachSelected: {},
  flexContainer: () => ({
    width: 'calc(93% - 4.25rem)',
    display: 'flex',
    marginLeft: '2.25rem',
    flexDirection: 'column',
    [`@media screen and (max-width: ${MAX_MEDIUM_SIZE}px)`]: {
      flexDirection: 'column',
    },
  }),
  fixMessageAction: {
    right: '0 !important',
  },
  hideDate: {
    display: 'none',
  },
  outreachData: {
    flex: 1,
    minWidth: 0,
    marginRight: '0.625rem',
  },
  messageFrom: {
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
    color: theme.palette.secondary.text,
  },
  badgesContainer: props => ({
    display: 'flex',
    gap: '0.25rem',
    marginBottom: props.detailOpen ? '0.5rem' : 0,
    order: props.detailOpen ? 2 : 3,
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      marginTop: '0.25rem',
      marginBottom: '0.5rem',
      marginLeft: 0,
    },
  }),
  badge: {
    height: '1.2rem',
    minWidth: '3rem',
  },
  opportunitybadge: {
    height: '1.2rem',
    paddingLeft: '0.2rem',
    paddingRight: '0.2rem',
  },
  outreachMessage: props => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    flexDirection: props.detailOpen ? 'column' : 'row',
    [`@media screen and (max-width: ${MAX_MEDIUM_SIZE}px)`]: {
      marginTop: '0.75rem',
    },
  }),
  outreachMessageMobile: {
    marginTop: '-0.25rem',
  },
  messageSubject: {
    color: theme.palette.secondary.text,
    fontWeight: 200,
    fontSize: '0.875rem',
    marginRight: '0.25rem',
    maxWidth: '60ch',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      width: '100%',
    },
  },
  messageBody: props => ({
    overflow: props.detailOpen ? 'unset' : 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1 1 0',
    color: theme.palette.secondary['900'],
    fontSize: '0.875rem',
    fontWeight: 500,
  }),
  outreachDate: {
    position: 'absolute',
    right: '1rem',
    top: '0.7rem',
    fontSize: '0.875rem',
    color: theme.palette.secondary['900'],
  },
  dot: {
    borderRadius: '50%',
    height: '0.75rem',
    width: '0.75rem',
    border: `1px solid ${theme.palette.secondary['300']}`,
    cursor: 'pointer',
    position: 'absolute',
    left: '1.25rem',
    top: '1.5rem',
  },
  readDot: {
    borderColor: theme.palette.secondary['300'],
    display: 'none',
  },
  unreadDot: {
    backgroundColor: theme.palette.primary.light,
    border: 'none',
  },
  checkbox: {
    position: 'absolute',
    top: '0.125rem',
    left: '0.375rem',
    zIndex: '98',
  },
  unselectedCheckbox: {
    display: 'none',
  },
  selectedCheckbox: {},
  outletTag: {
    marginTop: '-0.25rem',
    marginRight: '0.25rem',
  },
  fromName: {
    fontWeight: 200,
    marginRight: '0.5rem',
    display: 'inline-block',
  },
  outletName: {
    fontWeight: 500,
    color: theme.palette.secondary['900'],
    display: 'inline-block',
  },
  messageAction: {
    position: 'absolute',
    top: 0,
    right: '-400px',
    transition: 'right 0.3s',
  },
  messageWrapper: props => ({
    flex: 4,
    minWidth: 0,
    order: props.detailOpen ? 3 : 2,
    marginBottom: '0.625rem',
    [`@media screen and (max-width: ${MAX_MEDIUM_SIZE}px)`]: {
      marginTop: '-1rem',
      marginBottom: '0.25rem',
    },
  }),
}));

const OutreachRow = (props) => {
  const {
    outreach, lastMsg, toggleDetail, handleSelectClick, isDetailView,
    isSelected, selectedOutreachId, onClickOpportunityTag,
  } = props;

  const ability = useContext(AbilityContext);
  const isCurrentOutreachSelected = selectedOutreachId === outreach.id;
  const createdDate = new Date(lastMsg.received_date);
  const dateToDisplay = formatInboxTimestamp(createdDate);
  const classes = useStyles(props);

  const handleChangeAction = async (action) => {
    switch (action.value) {
      case ACTION_MARK_AS_READ:
        props.patchMessage(outreach.id, { is_read: !outreach.is_read });
        break;
      case ACTION_ARCHIVE:
        props.patchMessage(outreach.id, { is_archived: !outreach.is_archived });
        break;
      case ACTION_DELETE:
        props.handleDelete(outreach.id);
        break;
      default:
        break;
    }
  };

  const displayBadges = () => (
    <div className={classes.badgesContainer}>
      {outreach.outreach && (
        <ReplyStatusBadge
          status={outreach.reply_status}
          className={classes.badge}
          autoCategorized={!isEmpty(outreach.auto_categorized_date)}
        />
      )}
      {outreach.account && !isDetailView &&
        <Tag
          icon={<AccountIcon />}
          color="gray"
          className={classes.badge}
          label={outreach.account.name}
        />
      }
      {outreach.sequence &&
        <Tag
          icon={<SequenceIcon />}
          color="gray"
          className={classes.badge}
          label={
            ability.can('see', 'Sequence') ?
              <Link
                className={classes.customLink}
                to={sequenceTabUrl(outreach.sequence.id)}
                target="_blank"
                onClick={event => event.stopPropagation()}
              >
                {outreach.sequence.name}
              </Link> : outreach.sequence.name
          }
        />
      }
      {outreach.opportunity &&
        <Tag
          icon={<OpportunityIcon />}
          color="gray"
          className={classes.badge}
          onClick={onClickOpportunityTag}
          label={<span>{outreach.opportunity.title}</span>}
        />
      }
    </div>
  );

  return (
    <div
      key={lastMsg.id}
      aria-checked={isSelected}
      role="checkbox"
      tabIndex={-1}
      onKeyDown={() => { }}
      onClick={event => toggleDetail(event, lastMsg, outreach)}
      className={
        classNames(
          classes.outreachWrapper,
          { [classes.outreachUnread]: !outreach.is_read },
          { [classes.outreachSelected]: isCurrentOutreachSelected },
        )
      }
    >
      <Can I="select" a="Message">
        <Checkbox
          className={
            classNames(
              classes.checkbox,
              { [classes.selectedCheckbox]: isSelected },
              { [classes.unselectedCheckbox]: !isSelected },
            )
          }
          checked={isSelected}
          onChange={event => handleSelectClick(event, outreach.id)}
        />
      </Can>
      <div className={classes.flexContainer}>
        <div className={classes.outreachData}>
          <div className={classes.messageFrom}>
            <div className={classes.fromName}>
              {getMessageFromDisplayName(lastMsg.sent_from_email, lastMsg.sent_from_name)}
            </div>
            {outreach.outlet &&
              <div className={classes.outletName}>
                - {outreach.outlet.name}
              </div>
            }
          </div>
        </div>
        <div className={classes.messageWrapper}>
          <div className={classes.outreachMessage}>
            <div className={`${classes.messageSubject} text-truncate`}>{lastMsg.subject} -</div>
            <div className={classes.messageBody}>{lastMsg.body_plain}</div>
          </div>
        </div>
        {displayBadges()}
      </div>
      <div className={props.deleteOutreachId === outreach.id && props.isDeleteOpen ?
        classes.hideDate : classes.outreachDate}
      >
        {dateToDisplay}
      </div>
      <div className={classNames(
              classes.messageAction,
              { [classes.fixMessageAction]: props.deleteOutreachId === outreach.id && props.isDeleteOpen },
            )}
      >
        <MessageActionIcons
          isWhiteGlove={outreach.is_white_glove}
          isArchive={outreach.is_archived}
          isRead={outreach.is_read}
          handleChangeAction={handleChangeAction}
          fetchMessages={props.handleArchive}
        />
      </div>
    </div>
  );
};


OutreachRow.propTypes = {
  selectedOutreachId: PropTypes.string,
  toggleDetail: PropTypes.func.isRequired,
  handleSelectClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  outreach: PropTypes.shape({
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    id: PropTypes.string,
    outreach: PropTypes.string,
    messages: PropTypes.array,
    status_spam_date: PropTypes.string,
    reply_status: PropTypes.string.isRequired,
    pitch: PropTypes.shape({
      subject: PropTypes.string,
      body: PropTypes.string,
    }),
  }).isRequired,
  lastMsg: PropTypes.shape({
    id: PropTypes.string,
    status_spam_date: PropTypes.string,
    reply_status: PropTypes.string.isRequired,
    pitcdestroyMessageh: PropTypes.shape({
      subject: PropTypes.string,
      body: PropTypes.string,
    }),
  }).isRequired,
  patchMessage: PropTypes.func.isRequired,
  handleArchive: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  isDetailView: PropTypes.bool,
  deleteOutreachId: PropTypes.number,
  isDeleteOpen: PropTypes.bool,
  onClickOpportunityTag: PropTypes.func,
};


OutreachRow.defaultProps = {
  selectedOutreachId: null,
  isDetailView: false,
  deleteOutreachId: null,
  isDeleteOpen: false,
  onClickOpportunityTag: () => {},
};

export default compose(
  withRouter,
  withResponsive,
  React.memo,
  connect(
    state => ({
      selectedOutreachId: state.message.id,
    }),
    {
      patchMessage,
    },
  ),
)(OutreachRow);
